import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { StaatidDrivlinjeRow } from './api'
import { formatMonthName } from './api'
import { DRIVLINJE_FARGE, drivlinjeLabel, toChartData } from './drivlinjer'
import type { Point } from './drivlinjer'
import { isPartialMonth, formatNo } from '../partialPeriod'

interface Props {
  rows: StaatidDrivlinjeRow[]
  loading: boolean
  /** Newest date the data covers ("YYYY-MM-DD"), from useMaxDataDate. */
  maxDataDate?: string | null
}

export default function DrivlinjerChart({ rows, loading, maxDataDate }: Props) {
  const { t } = useTranslation('staatid')
  const [hidden, setHidden] = useState<Set<string>>(new Set())

  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="chart-skeleton" className="animate-pulse h-72 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  const data = toChartData(rows)
  // The current month is always incomplete after the nightly import; its
  // point is drawn hollow so it doesn't read as a real change.
  const partial = data.find(p => isPartialMonth(p.maned, maxDataDate))

  function toggle(gruppe: string) {
    setHidden(prev => {
      const next = new Set(prev)
      if (next.has(gruppe)) next.delete(gruppe)
      else next.add(gruppe)
      return next
    })
  }

  return (
    <div className="card p-4" data-testid="staatid-drivlinjer-chart">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('drivlinjer.chartTitle')}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="maned" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit=" d" />
          <Tooltip formatter={(v: number) => v.toLocaleString('nb-NO')} />
          <Legend
            wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
            onClick={e => toggle(String(e.dataKey))}
            formatter={(value: string, entry) => (
              <span style={{ opacity: hidden.has(String(entry.dataKey)) ? 0.4 : 1 }}>{value}</span>
            )}
          />
          {rows.map(r => {
            const farge = DRIVLINJE_FARGE[r.drivstoff_gruppe] ?? '#94a3b8'
            return (
              <Line
                key={r.drivstoff_gruppe}
                type="monotone"
                dataKey={r.drivstoff_gruppe}
                name={drivlinjeLabel(t, r.drivstoff_gruppe)}
                stroke={farge}
                strokeWidth={2}
                hide={hidden.has(r.drivstoff_gruppe)}
                isAnimationActive={false}
                activeDot={{ r: 5 }}
                dot={(props: { cx?: number; cy?: number; payload?: Point; index?: number }) => {
                  const { cx, cy, payload, index } = props
                  if (cx === undefined || cy === undefined) return <g key={index} />
                  const hollow = payload ? isPartialMonth(payload.maned, maxDataDate) : false
                  return (
                    <circle
                      key={index}
                      cx={cx}
                      cy={cy}
                      r={3}
                      stroke={farge}
                      strokeWidth={1.5}
                      fill={hollow ? 'transparent' : farge}
                    />
                  )
                }}
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
      {partial && maxDataDate && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-testid="staatid-drivlinjer-partial-note">
          <Trans
            t={t}
            i18nKey="drivlinjer.partialNote"
            values={{ month: formatMonthName(partial.maned), date: formatNo(maxDataDate) }}
            components={{ b: <strong /> }}
          />
        </p>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('drivlinjer.gapNote')}</p>
    </div>
  )
}
